import manifest from './storefront-images.json'

/**
 * One entry of the generated storefront image manifest.
 *
 * `images` is keyed by the source public path (query-string-free) and
 * maps to the optimized static AVIF the page should actually load, plus
 * the byte size and hashes the regression scripts verify.
 */
export interface StorefrontImageEntry {
  url: string
  width: number
  height: number
  bytes: number
  sha256: string
  source_sha256: string
}

interface StorefrontImagesManifest {
  preset: Record<string, unknown>
  images: Record<string, StorefrontImageEntry>
}

const data = manifest as StorefrontImagesManifest
const images = data.images ?? {}

/** Absolute, scheme-qualified or protocol-relative URLs are not ours to rewrite. */
const EXTERNAL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i

/**
 * Resolve a storefront image path to its optimized static AVIF.
 *
 * External (http/https/CDN), `blob:`, `data:` and every other scheme URL
 * come back untouched — ActiveStorage representations already point at
 * their own optimized AVIF. A static public path is looked up
 * query-string-free in the manifest; anything the manifest does not know
 * is returned exactly as it was given, cache-busting query and all.
 */
export function storefrontImage(src: string): string {
  if (!src || EXTERNAL.test(src)) return src
  const path = src.split('#')[0].split('?')[0]
  return images[path]?.url ?? src
}

/** Null-preserving convenience for optional image props. */
export function storefrontImageSrc(src: string | null | undefined): string | null {
  return src ? storefrontImage(src) : null
}
